import { Fragment } from 'react';
import { BASIC_PLAN, SILVER_PLAN, GOLD_PLAN } from './subscriptionPlans';

import freePlanImg from '../assets/plan_basic.svg';
import silverPlanImg from '../assets/plan_silver.svg';
import goldPlanImg from '../assets/plan_gold.svg';

import silverBadgeImg from '../assets/badge-icn.png';
import goldBadgeImg from '../assets/badge-icn-gold.png';

import insightsPersonal from '../assets/insights_personal.svg';
import insightsEnterprises from '../assets/insights_enterprises.svg';

const features_for_free = [
  {
    feature: ['Personalised news, filings, and research'],
  },
  {
    feature: ['Search by ticker, name, asset class, topic, sector, location'],
  },
  {
    feature: [
      'Powered by ',
      {
        text: 'natural language processing',
        href: '/nlp-nlu-machine-learning-for-financial-news-tweets-research',
      },
      ' and machine learning',
      {
        hasToolTip: true,
        tooltipText: (
          <>
            NLP and ML models plus our proprietary taxonomy provide better results than mere keyword
            searches.
            {' '}
            <a href="https://www.cityfalcon.ai/nlp-nlu-machine-learning-for-financial-news-tweets-research" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      }],
    display_inline: true,
  },
  {
    feature: [
      'Choose from thousands of quality sources in ',
      {
        text: '50+ languages',
        href: 'https://www.cityfalcon.ai/features/language-coverage',
      },
    ],
    display_inline: true,
  },
  {
    feature: ['Access on the web, on our Android and iOS apps, and on Amazon Echo,', 'Google Home, and Google Assistant'],
  },
  {
    feature: ['Up to 3 watchlists and 5 topics per watchlist'],
  },
  {
    feature: ['30 days search'],
  },
  {
    feature: [
      'Similar Story Grouping',
      {
        hasToolTip: true,
        tooltipText: (
          <>
            Machine learning groups similar stories together.
            {' '}
            <a href="https://help.cityfalcon.com/hc/en-us/articles/360007780737" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
    ],
    display_inline: true,
  },
  {
    feature: [
      'Basic Sentiment Analysis',
      {
        hasToolTip: true,
        tooltipText: (
          <>
            Sentiment shows how positive and negative a story or topic is.
            {' '}
            <a href="https://www.cityfalcon.ai/features/sentiment" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
    ],
    display_inline: true,
  },
  {
    feature: ['Financial Content Connected to Interactive Price Charts'],
  },
];

const features_for_silver = [
  {
    feature: ['No advertising on our site'],
  },
  {
    feature: ['Unlimited number of watchlists, with up to 100 topics per watchlist'],
  },
  {
    feature: [
      'Advanced Sentiment Analysis',
      {
        hasToolTip: true,
        tooltipText: (
          <>
            Sentiment shows how positive and negative a story or topic is.
            {' '}
            <a href="https://www.cityfalcon.ai/features/sentiment" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
    ],
    display_inline: true,
  },
  {
    feature: ['Custom push notifications and alerts'],
    display_block: true,
  },
  {
    feature: ['Premium topics and groups, e.g. S&P500 and its constituents'],
    coming_soon: true,
    display_block: true,
  },
  {
    feature: ['News insights and advanced analytics'],
    coming_soon: true,
    display_block: true,
  },
];

const features_for_gold = [
  {
    feature: [
      'Full text of select content from ',
      {
        text: '1000+ sources',
        href: 'https://help.cityfalcon.com/hc/en-us/articles/360034657073',
      },
      ' without paywalls or advertising',
    ],
    display_inline: true,
  },
  {
    feature: [
      'Key Headlines',
      {
        hasToolTip: true,
        tooltipText: (
          <>
            Key Headlines condense hundreds of stories into a few bullet points.
            {' '}
            <a href="https://help.cityfalcon.com/hc/en-us/articles/360006073397" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
    ],
    display_inline: true,
  },
  {
    feature: [
      'Investor Relations Presentations, Earnings, More',
      {
        hasToolTip: true,
        tooltipText: (
          <>
            Presentations, earnings calls, reports, and other important documents that appear on
            investor relations pages of corporate websites.
            <br />
            <a href="https://www.cityfalcon.ai/features/investor-relations" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
    ],
    display_inline: true,
  },
  {
    feature: ['Corporate ESG Presentations and Reports'],
  },
  {
    feature: ['Translation of all content into English'],
    display_block: true,
  },
  {
    feature: ['Offline reading on mobile and tablet'],
    display_block: true,
  },
  {
    feature: ['Access select content via API for personal use'],
    coming_soon: true,
    display_block: true,
  },
];

export const freePlan = {
  images: {
    bigImg: freePlanImg,
    mediumImg: freePlanImg,
    smallImg: freePlanImg,
  },
  text: 'free',
  basic: true,
  features: features_for_free,
  current_plan: BASIC_PLAN,
  head_title: 'basic plan',
  planPriceInfo: [
    {
      id: 789,
      plan_type: 'free',
    },
  ],
};

export const premiumPlans = [
  {
    images: {
      bigImg: silverPlanImg,
      mediumImg: silverPlanImg,
      smallImg: silverPlanImg,
    },
    text: 'silver',
    badgeImg: silverBadgeImg,
    comingSoon: true,
    silver: true,
    features: features_for_silver,
    current_plan: SILVER_PLAN,
    head_title: 'silver plan',
    planPriceInfo: [
      {
        active: true,
        billing_period: 1,
        billing_period_unit: 'month',
        price: 8,
        add_on_personal: 45,
        add_on_enterprises: 95,
        price_gbp: 6,
        add_on_personal_gbp: 34.5,
        add_on_enterprises_gbp: 73,
        plan_type: '1month',
        total_sub_text: 'After Free Trial',
        uk_addons_personal: 'uk-insights-personal-monthly',
        uk_addons_enterprises: 'uk-insights-enterprise-monthly',
        uk_addons_personal_gbp: 'uk-insights-p-1-m-gbp',
        uk_addons_enterprises_gbp: 'uk-insights-e-1-m-gbp',
        subText: (
          <>
            Intro price 20% off
            <br />
            Regular price $10
          </>
        ),
        subTextWithPlan: (
          <>
            Intro price 20% off
            <br />
            Regular price $10
          </>
        ),
        subTextWithPlanGbp: (
          <>
            Intro price 20% off
            <br />
            Regular price £7.5
          </>
        ),
      },
      {
        active: false,
        billing_period: 1,
        billing_period_unit: 'year',
        price: 6.5,
        offer: '2 months off!',
        add_on_personal: 37,
        add_on_enterprises: 79,
        price_gbp: 5,
        add_on_personal_gbp: 28.5,
        add_on_enterprises_gbp: 61,
        plan_type: '1year',
        total_sub_text: 'at 1 year',
        uk_addons_personal: 'uk-insights-personal-yearly',
        uk_addons_enterprises: 'uk-insights-enterprise-yearly',
        uk_addons_personal_gbp: 'uk-insights-p-1-y-gbp',
        uk_addons_enterprises_gbp: 'uk-insights-e-1-y-gbp',
        total: 80,
        total_gbp: 60,
        total_personal: 444,
        total_personal_gbp: 342,
        total_enterprises: 948,
        total_enterprises_gbp: 732,
        subText: '{{currencySymbol}}{{price}} {{currencyShort}}/year after trial',
        subTextWithPlan: '{{currencySymbol}}{{price}} {{currencyShort}}/year',
      },
      {
        active: false,
        billing_period: 2,
        billing_period_unit: 'year',
        price: 6,
        offer: '6 months off!',
        add_on_personal: 33,
        add_on_enterprises: 71,
        price_gbp: 4.5,
        add_on_personal_gbp: 25.5,
        add_on_enterprises_gbp: 54.5,
        plan_type: '2year',
        total_sub_text: 'at 2 years',
        uk_addons_personal: 'uk-insights-personal-2-years',
        uk_addons_enterprises: 'uk-insights-enterprise-2-years',
        uk_addons_personal_gbp: 'uk-insights-p-2-y-gbp',
        uk_addons_enterprises_gbp: 'uk-insights-e-2-y-gbp',
        total: 145,
        total_gbp: 108,
        total_personal: 792,
        total_personal_gbp: 612,
        total_enterprises: 1704,
        total_enterprises_gbp: 1308,
        subText: '{{currencySymbol}}{{price}} {{currencyShort}}/2 years after trial',
        subTextWithPlan: '{{currencySymbol}}{{price}} {{currencyShort}}/2 years',
      },
    ].map((v, i) => (v.id = i, v)),
  },
  {
    images: {
      bigImg: goldPlanImg,
      mediumImg: goldPlanImg,
      smallImg: goldPlanImg,
    },
    text: 'gold',
    badgeImg: goldBadgeImg,
    comingSoon: true,
    gold: true,
    features: features_for_gold,
    current_plan: GOLD_PLAN,
    head_title: 'gold plan',
    planPriceInfo: [
      {
        active: true,
        billing_period: 1,
        billing_period_unit: 'month',
        price: 19,
        add_on_personal: 45,
        add_on_enterprises: 95,
        price_gbp: 14.5,
        add_on_personal_gbp: 34.5,
        add_on_enterprises_gbp: 73,
        plan_type: '1month',
        uk_addons_personal: 'uk-insights-personal-monthly',
        uk_addons_enterprises: 'uk-insights-enterprise-monthly',
        uk_addons_personal_gbp: 'uk-insights-p-1-m-gbp',
        uk_addons_enterprises_gbp: 'uk-insights-e-1-m-gbp',
        subText: (
          <>
            Intro price 37% off
            <br />
            Regular price $30
          </>
        ),
        subTextWithPlanGbp: (
          <>
            Intro price 37% off
            <br />
            Regular price £23
          </>
        ),
        subTextWithPlan: (
          <>
            Intro price 37% off
            <br />
            Regular price $30
          </>
        ),
      },
      {
        active: false,
        billing_period: 1,
        billing_period_unit: 'year',
        price: 16,
        offer: '2 months off!',
        add_on_personal: 37,
        add_on_enterprises: 79,
        price_gbp: 12.5,
        add_on_personal_gbp: 28.5,
        add_on_enterprises_gbp: 61,
        plan_type: '1year',
        total_sub_text: 'at 1 year',
        uk_addons_personal: 'uk-insights-personal-yearly',
        uk_addons_enterprises: 'uk-insights-enterprise-yearly',
        uk_addons_personal_gbp: 'uk-insights-p-1-y-gbp',
        uk_addons_enterprises_gbp: 'uk-insights-e-1-y-gbp',
        total: 190,
        total_gbp: 150,
        total_personal: 444,
        total_personal_gbp: 342,
        total_enterprises: 948,
        total_enterprises_gbp: 732,
        subText: '{{currencySymbol}}{{price}} {{currencyShort}}/year',
        subTextWithPlan: '{{currencySymbol}}{{price}} {{currencyShort}}/year',
      },
      {
        active: false,
        billing_period: 2,
        billing_period_unit: 'year',
        price: 14,
        offer: '6 months off!',
        add_on_personal: 33,
        add_on_enterprises: 71,
        price_gbp: 11,
        add_on_personal_gbp: 25.5,
        add_on_enterprises_gbp: 54.5,
        plan_type: '2year',
        total_sub_text: 'at 2 years',
        uk_addons_personal: 'uk-insights-personal-2-years',
        uk_addons_enterprises: 'uk-insights-enterprise-2-years',
        uk_addons_personal_gbp: 'uk-insights-p-2-y-gbp',
        uk_addons_enterprises_gbp: 'uk-insights-e-2-y-gbp',
        total: 340,
        total_gbp: 264,
        total_personal: 792,
        total_personal_gbp: 612,
        total_enterprises: 1704,
        total_enterprises_gbp: 1308,
        total_text: '',
        subText: '{{currencySymbol}}{{price}} {{currencyShort}}/2 years',
        subTextWithPlan: '{{currencySymbol}}{{price}} {{currencyShort}}/2 years',
      },
    ].map((v, i) => (v.id = i, v)),
  },
];

export const FAQ = [
  {
    question: 'How do I sign up for the free trial?',
    answer: [
      `If you start a Silver plan, your free trial will start automatically. We want you to be confident in our
     product, and we’re so confident that you’ll be confident, we grant free trials to everyone. So all you need to do 
     is sign up for Silver, and you’ll be in Trial Mode for 7 days.`,
      {
        text: 'Please note due to overhead costs of Gold plans, we only offer free trials for Silver.',
        className: 'note_italic',
        href: '#',
      },
    ],
    id: 1,
  },
  {
    question: 'I had an account before CityFALCON Premium. What happens to my account?',
    answer: [
      `All CityFALCON accounts registered before the launch of Premium will automatically be converted to our 
       free Basic plan. If you want to upgrade, you can do so at any time.`,
    ],
    id: 2,
  },
  {
    question: 'Why is my account in Trial Mode?',
    answer: [
      `Since free trials are automatically granted, you’ll be in Trial Mode for the first seven days of your
       Silver subscription. Feel free to cancel anytime during this period, and your credit card won’t incur a charge.
       After the 7 days, your plan will become a regular subscription.`,
    ],
    id: 3,
  },
  {
    question: 'How can I see, change, or cancel my subscription?',
    answer: ['You can find your current Subscription through the ',
      {
        text: 'Subscription ',
        href: '#',
        className: 'italic',
      },
      'tab on the desktop platform or on the mobile app (under the ',
      {
        text: 'More ',
        href: '#',
        className: 'italic',
      },
      {
        text: 'tab). There is no need to leave CityFALCON’s platform.',
        href: '#',
        className: 'last',
      },
      'To change or cancel your subscription, do so in the ChargeBee portal. See our Knowledge Base for detailed instructions on ',
      {
        text: 'changing ',
        href: 'https://help.cityfalcon.com/hc/en-us/articles/360024315154-The-Subscription-and-Payment-Processes#changesubscription',
      },
      'and ',
      {
        text: 'cancelling ',
        href: 'https://help.cityfalcon.com/hc/en-us/articles/360024315154-The-Subscription-and-Payment-Processes#cancelsubscription',
      },
      'subscriptions.',
    ],
    id: 4,
  },
  {
    question: 'Can I change my subscription renewal period?',
    answer: [
      'Of course you can. Head to the Chargebee Portal and simply choose a different renewal period for the same plan level. ',
      {
        text: 'This tutorial ',
        href: 'https://help.cityfalcon.com/hc/en-us/articles/360024315154-The-Subscription-and-Payment-Processes#changesubscription',
      },
      'will walk you through making changes to subscriptions.',
    ],
    id: 5,
  },
  {
    question: 'Does Premium automatically renew?',
    answer: [
      `Yes, Premium subscriptions automatically renew based on the date of purchase and your choice of validity (monthly,
      annually, etc.).`,
    ],
    id: 6,
  },
  {
    question: 'Why should I pay you for content that is already public?',
    answer: [
      `Many news organisations and publishers have free versions of their service, but these are almost 
       invariably financed by advertisements. Advertisements slow down load times and cluttered the page. When you see
       public content through a Premium subscription, it will be advert-free, giving you less of a headache when reading
       lots of information.`,
    ],
    id: 7,
  },
  {
    question: 'Can I get a refund if I cancel?',
    answer: ['We cannot offer refunds or partial refunds for purchases. We make very few exceptions. Please see our ',
      {
        text: 'Premium Terms and Conditions ',
        href: '/terms#services-provided-under-premium-subscription-plans',
      }, ' and our ',
      {
        text: 'Premium Knowledge Base',
        href: 'https://help.cityfalcon.com/hc/en-us/categories/360001573013-CityFALCON-Premium-and-Subscriptions',
      },
      ' for more info.',
      {
        text:
          `The main exception is during the Trial Period for Silver plans. If you are in Trial Mode for a Silver
           plan, you may cancel at any time without penalty.`,
        href: '#',
        className: 'note',
      },
    ],
    id: 8,
  },
  {
    question: 'Are Premium features still available if I go back to a Basic plan?',
    answer: [
      `Yes, they will be available until the end of the current billing period! We don’t give refunds, but we do let 
        you access the stuff you paid for until your subscription runs out.`,
    ],
    id: 9,
  },
  {
    question: 'Is Premium available in all countries?',
    answer: [
      `You can use Premium content from almost any location. Our lawyers said we 
      have to tell you: our only exceptions are countries where UK companies are not allowed to do business (these are 
      countries under certain sanctions).`,
    ],
    id: 10,
  },
  {
    question: 'Can I share my Premium Content? Do you offer licenses?',
    answer: [
      `You should treat any copyrighted material on our platform as you would any other copyrighted material. So no, 
      you can’t share it by reproducing the content. Of course, you can share links to the content on social media or 
      email the links to your friends and coworkers. We don’t offer licenses, either, so if you want to reproduce works, 
      you’ll need to ask the publisher directly. You also can’t share an account. We have a one-person-one-account rule. 
      See our `,
      {
        text: 'Premium Terms and Conditions',
        href: '/terms#services-provided-under-premium-subscription-plans',
      },
      ' for more info.',
    ],
    id: 11,
  },
  {
    question: 'How does my subscription work across multiple devices?',
    answer: [
      `You can use your subscription across as many devices as you want, and your changes will even sync! We don’t
       recommend using multiple devices in the same session, though, because you might confuse yourself by setting 
       filters on one device that will sync across to another device where you are expecting different filters.`,
    ],
    id: 12,
  },
  {
    question: 'Why can I not upgrade from Android?',
    answer: [
      `We do not support upgrades from Android due to the high fees payable to mobile distribution platforms.
       Please sign up through our desktop or mobile website. `,
      {
        text: 'See our',
        className: 'italic',
      },
      {
        text: 'short tutorial',
        href: 'https://help.cityfalcon.com/hc/en-us/articles/360024879214-Video-Navigate-to-Chargebee-for-Mobile-Upgrades',
        linkClassName: 'inline-italic-link',
      },
      {
        text: ' for help.',
        className: 'italic',
      },
    ],
    id: 13,
  },
];

export const planTableHeader = {
  isHeader: true,
  images: {
    bigImg: freePlanImg,
    mediumImg: freePlanImg,
    smallImg: freePlanImg,
  },
  text: 'plan',
  plan: true,
  features: features_for_free,
  current_plan: null,
  head_title: 'Plans',
};

export const planTableData = [
  {
    isHeader: true,
    text: 'Limits',
    column: [
      { id: 1, text: 'Limits' }, { id: 2 }, { id: 3 }, { id: 4 },
    ],
  },
  {
    column: [
      { id: 1, text: 'Numbers of Watchlists' },
      { id: 2, text: '3' },
      { id: 3, text: 'Unlimited' },
      { id: 4, text: 'Unlimited' },
    ],
  },
  {
    column: [
      { id: 1, text: 'Topics per Watchlist' },
      { id: 2, text: '5' },
      { id: 3, text: '100' },
      { id: 4, text: '100' },
    ],
  },
  {
    column: [
      { id: 1, text: 'News Archive Search*' },
      { id: 2, text: '30 days' },
      { id: 3, text: '30 days' },
      { id: 4, text: '30 days' },
    ],
  },
  {
    isHeader: true,
    column: [
      { id: 1, text: 'Content' },
      { id: 2, text: '' },
      { id: 3, text: '' },
      { id: 4, text: '' },
    ],
  },
  {
    column: [
      { id: 1, text: 'Personalised News, filings, videos, and research' },
      { id: 2, text: '', isAvailable: true },
      { id: 3, text: '', isAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    column: [
      { id: 1, text: 'Number of Source Languages' },
      { id: 2, text: '30+' },
      { id: 3, text: '30+' },
      { id: 4, text: '30+' },
    ],
  },
  {
    className: 'plan_table_source',
    column: [
      { id: 1, text: 'Number of Sources' },
      { id: 2, text: '11000+' },
      { id: 3, text: '11000+' },
      { id: 4, text: '11000+' },
    ],
  },
  {
    className: 'plan_table_publication',
    column: [
      {
        id: 1,
        text: 'Premium Publications',
        className: 'plan_table_inner',
        isSubMenu: true,
        hasToolTip: true,
        tooltipText: (
          <>
            Publications that provide select content without paywalls or advertisements. See
            <a href="https://help.cityfalcon.com/hc/en-us/articles/360034657073-Publications-on-CityFALCON-Premium" target="_blank" rel="noopener noreferrer"> which publications </a>
            and
            <a href="https://help.cityfalcon.com/hc/en-us/articles/360002373117-FAQs-General#premiumcontentfaq" target="_blank" rel="noopener noreferrer"> which content.</a>
          </>
        ),
      },
      { id: 2, text: '', isNotAvailable: true },
      { id: 3, text: '', isNotAvailable: true },
      { id: 4, text: '1000+' },
    ],
  },
  {
    column: [
      { id: 1, text: 'Full-text and offline reading directly on CityFALCON' },
      { id: 2, text: '', isNotAvailable: true },
      { id: 3, text: '', isNotAvailable: true },
      {
        id: 4,
        text: 'For select premium content',
        hasToolTip: true,
        tooltipText: (
          <>
            Not all content from Premium Publications is available offline or
            advert- and paywall-free. See
            <a
              href="https://help.cityfalcon.com/hc/en-us/articles/360002373117-FAQs-General#premiumcontentfaq"
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}
              here
              {' '}
            </a>
            for details.
          </>
        ),
      },
    ],
  },
  {
    column: [
      { id: 1, text: 'Free of Paywalls and Advertisement' },
      { id: 2, text: '', isNotAvailable: true },
      { id: 3, text: '', isNotAvailable: true },
      {
        id: 4,
        text: 'For select premium content',
        hasToolTip: true,
        tooltipText: (
          <>
            Not all content from Premium Publications is available offline or
            advert- and paywall-free. See
            <a
              href="https://help.cityfalcon.com/hc/en-us/articles/360002373117-FAQs-General#premiumcontentfaq"
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}
              here
              {' '}
            </a>
            for details.
          </>
        ),
      },
    ],
  },
  {
    isHeader: true,
    column: [
      { id: 1, text: 'Tools and Analytics' },
      { id: 2, text: '' },
      { id: 3, text: '' },
      { id: 4, text: '' },
    ],
  },
  {
    column: [
      {
        id: 1,
        text: 'Curation Tools',
        hasToolTip: true,
        tooltipText: (
          <>
            <a
              href="https://help.cityfalcon.com/hc/en-us/articles/360002615258-Curation-Tools"
              target="_blank"
              rel="noopener noreferrer"
            >
              {' '}
              Curation tools
              {' '}
            </a>
            train our algorithms to deliver you exactly what you need.
          </>
        ),
      },
      { id: 2, text: '', isAvailable: true },
      { id: 3, text: '', isAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    column: [
      {
        id: 1,
        text: 'CityFALCON Relevance Score',
        hasToolTip: true,
        tooltipText: (
          <>
            Our
            <a href="https://help.cityfalcon.com/hc/en-us/articles/360007925074" target="_blank" rel="noopener noreferrer"> CityFALCON Score </a>
            quantifies the relevance of content based on your user profile.
          </>
        ),
      },
      { id: 2, text: '', isAvailable: true },
      { id: 3, text: '', isAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    column: [
      {
        id: 1,
        text: 'Sentiment Analysis',
        className: 'plan_table_sentiment',
        hasToolTip: true,
        tooltipText: (
          <>
            Sentiment shows how positive and negative a story or topic is.
            {' '}
            <a href="https://www.cityfalcon.ai/features/sentiment" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
      { id: 2, text: 'Limited' },
      { id: 3, text: 'Full' },
      { id: 4, text: 'Full' },
    ],
  },
  {
    column: [
      {
        id: 1,
        text: 'Grouping of Similar Stories',
        hasToolTip: true,
        tooltipText: (
          <>
            Machine learning groups similar stories together.
            {' '}
            <a href="https://help.cityfalcon.com/hc/en-us/articles/360007780737" target="_blank" rel="noopener noreferrer">Learn more.</a>
          </>
        ),
      },
      { id: 2, text: '', isAvailable: true },
      { id: 3, text: '', isAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    column: [
      { id: 1, text: 'Translation into English' },
      { id: 2, text: '', isNotAvailable: true },
      { id: 3, text: '', isNotAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    column: [
      {
        id: 1,
        text: 'Spam Detection',
        isCommingSoon: true,
        className: 'plan_table_spam',
      },
      { id: 2, text: '', isNotAvailable: true },
      { id: 3, text: '', isAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    column: [
      {
        id: 1,
        text: 'Advanced Analytics',
        isCommingSoon: true,
        className: 'plan_table_advance_analytics',
      },
      { id: 2, text: '', isNotAvailable: true },
      { id: 3, text: '', isAvailable: true },
      { id: 4, text: '', isAvailable: true },
    ],
  },
  {
    isHeader: true,
    column: [
      { id: 1, text: '' },
      { id: 2, text: '' },
      { id: 3, text: '' },
      { id: 4, text: '' },
    ],
  },
  {
    className: 'plan_table_row_platforms',
    column: [
      { id: 1, text: 'Platforms', className: 'plan_table_column_bold' },
      { id: 2, text: (<div>Web, Voice, Mobile</div>), className: 'plan_table_column_platform' },
    ],
  },
  {
    isHeader: true,
    column: [
      { id: 1, text: '' },
      { id: 2, text: '' },
      { id: 3, text: '' },
      { id: 4, text: '' },
    ],
  },
  {
    column: [
      { id: 1, text: 'Free Trials', className: 'plan_table_column_bold' },
      { id: 2, text: 'Always free' },
      { id: 3, text: '7 days' },
      { id: 4, text: '', isNotAvailable: true },
    ],
  },
  {
    isHeader: true,
    column: [
      { id: 1, text: '' },
      { id: 2, text: '' },
      { id: 3, text: '' },
      { id: 4, text: '' },
    ],
  },
  {
    className: 'plan_table_row_enterprice',
    column: [
      { id: 1, text: 'Enterprises and Organisations', className: 'plan_table_column_bold' },
      {
        id: 2,
        text: (
          <>
            <p>
              Multi-user packages are available, featuring Single Sign-on (SSO)
              for seamless integration with existing systems.
              <a href="/contact-us"> Contact us </a>
              for details.
            </p>
          </>
        ),
        className: 'plan_table_column_enterprice',
      },
    ],
  },
  {
    isHeader: true,
    column: [
      { id: 1, text: '' },
      { id: 2, text: '' },
      { id: 3, text: '' },
      { id: 4, text: '' },
    ],
  },
].map((v, i) => ({ ...v, id: i }));

export const InsightsList = [
  'Access to millions of companies, filings, and more from Companies House UK',
  'Key filings information is extracted by machine, cleanly presented, and visualised',
  'Examine capital and share structure and alternative data',
  'Explore first-in-the-industry share price and market capitalisation graphs for private companies',
  'Drill down into the portfolios of prominent investors',
  'Find the companies with the highest ROIs and largest fundraises to discover the next potential unicorns',
];

export const InsightsPlans = {
  personal: {
    title: 'Personal/Startup',
    iconLink: insightsPersonal,
    price: '33-45',
    priceGbp: '25.5-34.5',
    price1Month: '45',
    price1MonthGbp: '34.5',
    price1Year: '444',
    price1YearGbp: '342',
    price1YearByMonth: '37',
    price1YearByMonthGbp: '28.5',
    price2Year: '792',
    price2YearGbp: '612',
    price2YearByMonth: '33',
    price2YearByMonthGbp: '25.5',
    addons: {
      uk_addons_1month: 'uk-insights-personal-monthly',
      uk_addons_1year: 'uk-insights-personal-yearly',
      uk_addons_2year: 'uk-insights-personal-2-years',
      uk_addons_1month_gbp: 'uk-insights-p-1-m-gbp',
      uk_addons_1year_gbp: 'uk-insights-p-1-y-gbp',
      uk_addons_2year_gbp: 'uk-insights-p-2-y-gbp',
    },
  },
  enterprises: {
    title: 'Enterprises',
    iconLink: insightsEnterprises,
    price: '71-95',
    priceGbp: '54.5-73',
    price1Month: '95',
    price1MonthGbp: '73',
    price1Year: '948',
    price1YearGbp: '732',
    price1YearByMonth: '79',
    price1YearByMonthGbp: '61',
    price2Year: '1704',
    price2YearGbp: '1308',
    price2YearByMonth: '71',
    price2YearByMonthGbp: '54.5',
    addons: {
      uk_addons_1month: 'uk-insights-enterprise-monthly',
      uk_addons_1year: 'uk-insights-enterprise-yearly',
      uk_addons_2year: 'uk-insights-enterprise-2-years',
      uk_addons_1month_gbp: 'uk-insights-e-1-m-gbp',
      uk_addons_1year_gbp: 'uk-insights-e-1-y-gbp',
      uk_addons_2year_gbp: 'uk-insights-e-2-y-gbp',
    },
  },
};
